<template>
  <div class="volunteer-signup-view">
    <div class="form-container">
      <div class="form-header">
        <h1>Volunteer With Us</h1>
        <p>Join our team of volunteers helping provide technology access to students</p>
      </div>

      <div v-if="successMessage" class="success-message">
        {{ successMessage }}
      </div>

      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>

      <form v-if="!successMessage" @submit.prevent="submitSignup" class="volunteer-form">
        <!-- Contact Information -->
        <section class="form-section">
          <h2>Contact Information</h2>
          <div class="form-row">
            <div class="form-group full-width">
              <label for="name">Full Name *</label>
              <input
                id="name"
                v-model="formData.name"
                type="text"
                required
                placeholder="Enter your full name"
              >
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email">Email *</label>
              <input
                id="email"
                v-model="formData.email"
                type="email"
                required
                placeholder="your-email@example.com"
              >
            </div>
            <div class="form-group">
              <label for="phone">Phone</label>
              <input
                id="phone"
                v-model="formData.phone"
                type="tel"
                placeholder="(321) 555-1234"
              >
            </div>
          </div>
        </section>

        <!-- How You Want to Help -->
        <section class="form-section">
          <h2>How Would You Like to Help?</h2>
          <p class="section-description">Select any areas where you'd like to volunteer (optional)</p>

          <div class="skills-checkboxes">
            <label class="checkbox-label">
              <input
                type="checkbox"
                value="computer_refurbishment"
                v-model="formData.interested_in"
              >
              <div class="skill-info">
                <span class="skill-label">Computer Refurbishment</span>
                <span class="skill-description">Help refurbish, repair, and prepare computers for distribution</span>
              </div>
            </label>

            <label class="checkbox-label">
              <input
                type="checkbox"
                value="distribution_delivery"
                v-model="formData.interested_in"
              >
              <div class="skill-info">
                <span class="skill-label">Distribution/Delivering</span>
                <span class="skill-description">Assist with delivering laptops to students and schools</span>
              </div>
            </label>

            <label class="checkbox-label">
              <input
                type="checkbox"
                value="stem_mentoring"
                v-model="formData.interested_in"
              >
              <div class="skill-info">
                <span class="skill-label">STEM Mentoring</span>
                <span class="skill-description">Mentor students in technology and STEM-related areas</span>
              </div>
            </label>

            <label class="checkbox-label">
              <input
                type="checkbox"
                value="stem_workshops"
                v-model="formData.interested_in"
              >
              <div class="skill-info">
                <span class="skill-label">STEM Workshops/Career Days</span>
                <span class="skill-description">Help organize and participate in workshops and career day events</span>
              </div>
            </label>
          </div>
        </section>

        <!-- Additional Comments -->
        <section class="form-section">
          <h2>Additional Information</h2>
          <div class="form-group">
            <label for="comments">Comments</label>
            <textarea
              id="comments"
              v-model="formData.comments"
              rows="4"
              placeholder="Tell us about your availability, interests, or anything else you'd like us to know..."
            ></textarea>
          </div>
        </section>

        <!-- Submit Button -->
        <div class="form-actions">
          <button type="submit" :disabled="submitting" class="btn-submit">
            {{ submitting ? 'Submitting...' : 'Sign Up to Volunteer' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import axios from 'axios'

const formData = ref({
  name: '',
  email: '',
  phone: '',
  interested_in: [] as string[],
  comments: ''
})

const submitting = ref(false)
const successMessage = ref('')
const errorMessage = ref('')

const submitSignup = async () => {
  submitting.value = true
  errorMessage.value = ''
  successMessage.value = ''

  try {
    const response = await axios.post('/api/v1/volunteers/signup', formData.value)

    successMessage.value = response.data.message || 'Thank you for signing up! We will contact you about volunteer opportunities.'

    // Reset form
    formData.value = {
      name: '',
      email: '',
      phone: '',
      interested_in: [],
      comments: ''
    }

    // Scroll to top to show success message
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (error: any) {
    console.error('Error submitting volunteer signup:', error)
    errorMessage.value = error.response?.data?.detail || 'Failed to submit signup. Please try again.'
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Inter:wght@400;500;600&display=swap');

.volunteer-signup-view {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem 1rem;
}

.form-container {
  max-width: 800px;
  margin: 0 auto;
  background: var(--white, #ffffff);
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  padding: 2rem;
  text-align: center;
  color: var(--white, #ffffff);
}

.form-header h1 {
  margin: 0 0 0.5rem 0;
  font-family: 'Poppins', sans-serif;
  font-size: 2rem;
}

.form-header p {
  margin: 0;
  opacity: 0.95;
  font-family: 'Inter', sans-serif;
}

.success-message {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
  padding: 1.5rem 2rem;
  margin: 2rem;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
  font-size: 1.1rem;
  line-height: 1.6;
}

.error-message {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
  padding: 1rem 1.5rem;
  margin: 1.5rem 1.5rem 0 1.5rem;
  border-radius: 6px;
  font-family: 'Inter', sans-serif;
}

.volunteer-form {
  padding: 2rem;
}

.form-section {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid #e0e0e0;
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section h2 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.3rem;
  color: var(--primary-color, #2c4a6b);
  margin: 0 0 0.5rem 0;
}

.section-description {
  color: var(--text-light, #666666);
  margin: 0 0 1rem 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-dark, #333333);
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
}

.form-group input,
.form-group textarea {
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 1rem;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.3s;
  background: var(--white, #ffffff);
  color: var(--text-dark, #333333);
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: var(--secondary-color, #3ba3d1);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
}

.skills-checkboxes {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
  border: 1px solid transparent;
}

.checkbox-label:hover {
  background: var(--light-bg, #f5f7fa);
  border-color: #e0e0e0;
}

.skill-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
}

.skill-label {
  font-weight: 600;
  color: var(--text-dark, #333333);
  font-size: 0.95rem;
}

.skill-description {
  font-size: 0.85rem;
  color: var(--text-light, #666666);
  line-height: 1.4;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  border: 2px solid var(--secondary-color, #3ba3d1);
  border-radius: 3px;
  background: var(--white, #ffffff);
  position: relative;
  margin: 0;
  flex-shrink: 0;
}

.checkbox-label input[type="checkbox"]:checked {
  background: var(--secondary-color, #3ba3d1);
  border-color: var(--secondary-color, #3ba3d1);
}

.checkbox-label input[type="checkbox"]:checked::after {
  content: '✓';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 14px;
  font-weight: bold;
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.btn-submit {
  padding: 1rem 3rem;
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  border: none;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s;
  font-family: 'Inter', sans-serif;
}

.btn-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(59, 163, 209, 0.4);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .volunteer-signup-view {
    padding: 1rem 0.5rem;
  }

  .form-header h1 {
    font-size: 1.5rem;
  }

  .volunteer-form {
    padding: 1.5rem 1rem;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .btn-submit {
    width: 100%;
  }
}
</style>
